import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  Mail,
  Send,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UserPlus,
  UserMinus,
  Users,
  Trash2,
  Pencil,
} from "lucide-react";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { useNeed, useNeedMatches } from "@/hooks/useStaffing";
import apiClient, { normalizeApiError } from "@/lib/api";
import {
  advanceApplicationStage,
  backApplicationStage,
  createApplication,
  decideApplication,
  detachApplication,
  detachCandidateFromNeed,
  validateApplicationCommercial,
  validateApplicationTech,
} from "@/lib/recruitmentApi";
import StartRecruitmentModal from "@/components/admin/recruitment/StartRecruitmentModal";
import NeedEditModal from "@/components/admin/recruitment/NeedEditModal";
import ClientPresentationModal from "@/components/admin/recruitment/ClientPresentationModal";
import EmailPreviewModal from "@/components/admin/recruitment/EmailPreviewModal";
import InterviewsWorkflowModal from "@/components/admin/recruitment/InterviewsWorkflowModal";
import AcceptanceConfirmationModal from "@/components/admin/recruitment/AcceptanceConfirmationModal";
import MissionConversionModal from "@/components/admin/recruitment/MissionConversionModal";

const STAGES_V2 = [
  { id: "qualification", label: "Qualification RH", role: "RH" },
  { id: "validation_tech", label: "Validation Tech", role: "CTO" },
  { id: "validation_commercial", label: "Validation Comm.", role: "CCO" },
  { id: "preparation_cv", label: "Préparation CV", role: "RH" },
  { id: "presente", label: "Présenté Client", role: "COMMERCIAL" },
  { id: "entretiens", label: "Entretiens", role: "RH" },
  { id: "decision", label: "Décision Finale", role: "DIRECTION" },
];

const STAGE_COLORS = {
  qualification: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  validation_tech: "bg-violet-500/10 text-violet-300 border-violet-500/20",
  validation_commercial: "bg-purple-500/10 text-purple-300 border-purple-500/20",
  preparation_cv: "bg-sky-500/10 text-sky-300 border-sky-500/20",
  presente: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  entretiens: "bg-indigo-500/10 text-indigo-300 border-indigo-500/20",
  decision: "bg-rose-500/10 text-rose-300 border-rose-500/20",
  accepted: "bg-teal-500/10 text-teal-300 border-teal-500/20",
  refuse_client: "bg-red-500/10 text-red-300 border-red-500/20",
  rejected: "bg-red-500/10 text-red-300 border-red-500/20",
  retire: "bg-slate-500/10 text-slate-300 border-slate-500/20",
};

function renderCommercialTerms(terms) {
  if (!terms) return null;
  if (typeof terms === 'string') return terms;
  if (typeof terms === 'object') {
    if (terms.notes) return terms.notes;
    const parts = [];
    if (terms.free_days != null && Number(terms.free_days) > 0) {
      parts.push(`${terms.free_days} jour(s) de gratuité`);
    }
    if (terms.mission_type) {
      parts.push(`Mission ${terms.mission_type === 'court' ? 'courte' : 'longue'}`);
    }
    if (terms.tjm_client && terms.mode) {
      parts.push(`${terms.tjm_client} €/j (${terms.mode === 'fixed' ? 'Fixe' : 'Maximum'})`);
    }
    return parts.length > 0 ? parts.join(' • ') : null;
  }
  return String(terms);
}

export default function NeedDetailPage() {
  const { needId } = useParams();
  const { need, loading, error } = useNeed(needId);
  const navigate = useNavigate();

  const [localNeed, setLocalNeed] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const activeNeed = localNeed || need;

  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);

  const [showPositionModal,setShowPositionModal] = useState(false);

  // Modals state
  const [selectedAppForPresentation, setSelectedAppForPresentation] = useState(null);
  const [selectedAppForRefusal, setSelectedAppForRefusal] = useState(null);
  const [selectedAppForInterview, setSelectedAppForInterview] = useState(null);
  const [selectedAppForAcceptance, setSelectedAppForAcceptance] = useState(null);
  const [selectedAppForConversion, setSelectedAppForConversion] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingNeed, setDeletingNeed] = useState(false);

  // Detachment modal state
  const [selectedAppForDetach, setSelectedAppForDetach] = useState(null);
  const [detachReason, setDetachReason] = useState("Profil non retenu par le client");
  const [detachCustomReason, setDetachCustomReason] = useState("");
  const [detachError, setDetachError] = useState(null);
  const [detaching, setDetaching] = useState(false);

  // Action status / feedback
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);

  const fetchApplications = useCallback(async () => {
    if (!needId) return;
    setLoadingApps(true);
    try {
      const res = await apiClient.get("/recruitment/applications", {
        params: { need_id: needId },
      });
      setApplications(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch applications for need:", err);
    } finally {
      setLoadingApps(false);
    }
  }, [needId]);

  useEffect(() => {
    void fetchApplications();
  }, [fetchApplications]);

  const handleDetachCandidate = async () => {
    if (!selectedAppForDetach || detaching) return;
    setDetaching(true);
    setActionError(null);
    setDetachError(null);
    try {
      const finalReason = detachReason === "Autre" ? (detachCustomReason.trim() || "Autre motif") : detachReason;
      const appId = selectedAppForDetach._id || selectedAppForDetach.id;
      const candId = selectedAppForDetach.candidate_id || selectedAppForDetach.candidate?._id;

      let success = false;
      try {
        await detachApplication(appId, finalReason, { need_id: needId, candidate_id: candId });
        success = true;
      } catch (primaryErr) {
        const status = primaryErr?.response?.status;
        // Only fallback to detachCandidateFromNeed if the application endpoint returned 404 (not found)
        if (status === 404 && needId && candId) {
          await detachCandidateFromNeed(needId, candId, finalReason);
          success = true;
        } else {
          throw primaryErr;
        }
      }

      if (success) {
        setActionSuccess("Candidat détaché du besoin avec succès. Le profil et ses CVs sont conservés dans LENVIA.");
        setSelectedAppForDetach(null);
        setDetachReason("Profil non retenu par le client");
        setDetachCustomReason("");
        setDetachError(null);
        await fetchApplications();
        setTimeout(() => setActionSuccess(null), 4000);
      }
    } catch (err) {
      const normalized = normalizeApiError(err, "Échec du détachement du candidat.");
      setDetachError(normalized.message);
      setActionError(normalized.message);
    } finally {
      setDetaching(false);
    }
  };

  const handleLinkCandidate = async (candidateId) => {
    setActionError(null);
    try {
      await createApplication({
        candidate_id: candidateId,
        need_id: needId,
      });
      setActionSuccess("Candidat positionné avec succès sur ce besoin.");
      await fetchApplications();
      setTimeout(() => setActionSuccess(null), 4000);
    } catch (err) {
      setActionError(err?.response?.data?.detail || "Échec du positionnement du candidat.");
    }
  };

  const handleAdvance = async (appId) => {
    setActionError(null);
    try {
      await advanceApplicationStage(appId);
      await fetchApplications();
    } catch (err) {
      setActionError(err?.response?.data?.detail || "Transition d'étape refusée par le serveur.");
    }
  };

  const handleBack = async (appId) => {
    setActionError(null);
    try {
      await backApplicationStage(appId, "Retour arrière demandé par le recruteur");
      await fetchApplications();
    } catch (err) {
      setActionError(err?.response?.data?.detail || "Retour d'étape refusé par le serveur.");
    }
  };

  const handleValidateTech = async (appId, approved) => {
    setActionError(null);
    try {
      await validateApplicationTech(appId, approved, approved ? "Validation CTO accordée" : "Profil jugé insuffisant techniquement");
      await fetchApplications();
    } catch (err) {
      setActionError(err?.response?.data?.detail || "Validation technique refusée (droits insuffisants).");
    }
  };

  const handleValidateCommercial = async (appId, approved) => {
    setActionError(null);
    try {
      await validateApplicationCommercial(appId, approved, approved ? "Accord commercial CCO accordé" : "Inadéquation TJM/profil client");
      await fetchApplications();
    } catch (err) {
      setActionError(err?.response?.data?.detail || "Validation commerciale refusée (droits insuffisants).");
    }
  };

  const handleDecide = async (appId, outcome) => {
    setActionError(null);
    try {
      await decideApplication(appId, outcome, "internal", "Décision finale enregistrée");
      await fetchApplications();
    } catch (err) {
      setActionError(err?.response?.data?.detail || "Enregistrement de la décision refusé.");
    }
  };

  const handleDeleteNeed = async () => {
    setDeletingNeed(true);
    setActionError(null);
    try {
      await apiClient.delete(`/recruitment/needs/${needId}`);
      navigate("/admin/recrutement/besoins");
    } catch (err) {
      setActionError(
        err?.response?.data?.detail || "Échec de la suppression du besoin client."
      );
      setShowDeleteModal(false);
    } finally {
      setDeletingNeed(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !activeNeed) {
    return (
      <div className="p-6 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-sm">
        {error || "Besoin client introuvable."}
      </div>
    );
  }

  const reqs = activeNeed.requirements || {};

  return (
    <div className="space-y-8 pb-16" data-testid="need-detail-page">
      {/* Navigation Breadcrumb */}
      <div className="flex items-center gap-3">
        <Link
          to="/admin/recrutement/besoins"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour aux besoins
        </Link>
        <span className="text-slate-600">/</span>
        <span className="text-xs text-blue-400 font-mono truncate">{activeNeed.title}</span>
      </div>

      {actionSuccess && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>{actionSuccess}</span>
        </div>
      )}

      {actionError && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{actionError}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowEditModal(true)}
            className="min-h-11 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-semibold text-xs border border-white/20 flex items-center gap-2 transition-colors shadow-sm"
            data-testid="edit-need-btn"
          >
            <Pencil className="h-4 w-4 text-blue-400" />
            Modifier le besoin
          </button>
          <button className="min-h-11 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs transition-colors" onClick={() => setShowPositionModal(true)}>Positionner un candidat</button>
          <a className="min-h-11 px-4 py-3 rounded-lg border border-white/20 text-blue-200 text-xs font-semibold hover:bg-white/5 transition-colors" href="#matching-candidates">Rechercher des candidats correspondants</a>
        </div>
        <button
          type="button"
          onClick={() => setShowDeleteModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 border border-rose-500/20 rounded-xl transition-colors text-xs font-semibold"
          title="Supprimer le besoin"
          data-testid="delete-need-detail-btn"
        >
          <Trash2 className="h-4 w-4" />
          Supprimer ce besoin
        </button>
      </div>
      {showPositionModal && <StartRecruitmentModal needId={needId} onClose={() => setShowPositionModal(false)} />}
      {showEditModal && activeNeed && (
        <NeedEditModal
          isOpen={showEditModal}
          need={activeNeed}
          applicationsCount={applications.length}
          onClose={() => setShowEditModal(false)}
          onUpdated={(updated) => {
            setLocalNeed(updated);
            setActionSuccess("Besoin client modifié avec succès.");
            setTimeout(() => setActionSuccess(null), 4000);
          }}
        />
      )}
      {/* Need Detail Header Card */}
      <section className="glass rounded-3xl p-6 lg:p-8 border border-white/10 space-y-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="px-3 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                {activeNeed.status || "open"}
              </span>
              {activeNeed.client_id && (
                <span className="px-2.5 py-0.5 rounded-md text-xs font-mono bg-white/5 text-blue-300 border border-white/10">
                  Client : {activeNeed.client_id}
                </span>
              )}
              {activeNeed.input_mode && (
                <span className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-purple-500/10 text-purple-300 border border-purple-500/20">
                  {activeNeed.input_mode === "quick" ? "Saisie Rapide (IA)" : "Formulaire Structuré"}
                </span>
              )}
              {reqs.role_identified && (
                <span className="px-2.5 py-0.5 rounded-md text-[11px] font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                  Rôle analysé : {reqs.role_identified}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-heading font-extrabold text-white tracking-tight" data-testid="need-title-display">
              {activeNeed.title}
            </h1>
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-slate-300 shrink-0">
            {(activeNeed.contract_types?.length || activeNeed.contract_type) && (
              <div className="p-3 rounded-xl bg-white/5 border border-white/5">
                <span className="text-slate-500 block">Contrat</span>
                <span className="font-semibold text-white">{(activeNeed.contract_types || [activeNeed.contract_type]).join(" · ")}</span>
              </div>
            )}
            {activeNeed.start_date && (
              <div className="p-3 rounded-xl bg-white/5 border border-white/5">
                <span className="text-slate-500 block">Démarrage</span>
                <span className="font-semibold text-white">{activeNeed.start_date}</span>
              </div>
            )}
          </div>
        </div>

        {activeNeed.description && (
          <div className="pt-2 border-t border-white/5">
            <p className="text-xs text-slate-300 whitespace-pre-line leading-relaxed max-w-4xl">
              {activeNeed.description}
            </p>
          </div>
        )}

        {/* Structured requirements tags */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-3 border-t border-white/5 text-xs">
          {reqs.responsibilities?.length > 0 && (
            <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
              <span className="text-[11px] font-semibold text-indigo-400 uppercase tracking-wider block mb-1.5">
                Responsabilités principales
              </span>
              <div className="flex flex-wrap gap-1">
                {reqs.responsibilities.map((s, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-[11px]">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <span className="text-[11px] font-semibold text-blue-400 uppercase tracking-wider block mb-1.5">
              Technologies / Compétences obligatoires
            </span>
            <div className="flex flex-wrap gap-1">
              {(reqs.mandatory || activeNeed.required_skills || []).map((s, i) => (
                <span key={i} className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30 text-[11px]">
                  {s}
                </span>
              ))}
            </div>
          </div>
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <span className="text-[11px] font-semibold text-cyan-400 uppercase tracking-wider block mb-1.5">
              Nécessaires ou Recommandées
            </span>
            <div className="flex flex-wrap gap-1">
              {(reqs.recommended || []).length > 0 ? (
                reqs.recommended.map((s, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 text-[11px]">
                    {s}
                  </span>
                ))
              ) : (
                <span className="text-slate-500 italic text-[11px]">Aucune spécifiée</span>
              )}
            </div>
          </div>
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <span className="text-[11px] font-semibold text-emerald-400 uppercase tracking-wider block mb-1.5">
              Bonus / Atouts
            </span>
            <div className="flex flex-wrap gap-1">
              {(reqs.bonus || []).length > 0 ? (
                reqs.bonus.map((s, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[11px]">
                    {s}
                  </span>
                ))
              ) : (
                <span className="text-slate-500 italic text-[11px]">Aucune</span>
              )}
            </div>
          </div>
          {reqs.context_technologies?.length > 0 && (
            <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
              <span className="text-[11px] font-semibold text-amber-400 uppercase tracking-wider block mb-1.5">
                Technologies de contexte (environnement)
              </span>
              <div className="flex flex-wrap gap-1">
                {reqs.context_technologies.map((s, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[11px]">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <span className="text-[11px] font-semibold text-violet-400 uppercase tracking-wider block mb-1.5">
              Fonctionnelles / Métier
            </span>
            <div className="flex flex-wrap gap-1">
              {(reqs.functional || []).length > 0 ? (
                reqs.functional.map((s, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/30 text-[11px]">
                    {s}
                  </span>
                ))
              ) : (
                <span className="text-slate-500 italic text-[11px]">Aucune</span>
              )}
            </div>
          </div>
        </div>

        {/* Commercial conditions & client details */}
        {(activeNeed.tjm_client != null || activeNeed.tjm_client_mode || activeNeed.commercial_terms || activeNeed.client_contact_id || activeNeed.company_name || activeNeed.free_days != null || activeNeed.mission_type) && (
          <div className="pt-3 border-t border-white/5 flex flex-wrap items-center gap-3 text-xs">
            {activeNeed.company_name && (
              <div className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
                <span className="text-slate-500 block text-[10px] uppercase font-semibold">Entreprise</span>
                <span className="font-semibold text-white">
                  {activeNeed.company_name} {activeNeed.company_type ? `(${String(activeNeed.company_type).toLowerCase() === "client" ? "Client actif" : "Prospect"})` : ""}
                </span>
              </div>
            )}
            <div className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
              <span className="text-slate-500 block text-[10px] uppercase font-semibold">TJM Client</span>
              <span className="font-semibold text-white">
                {activeNeed.tjm_client_mode === 'unknown' || activeNeed.tjm_client == null
                  ? 'Non renseigné'
                  : `${activeNeed.tjm_client} €/j (${activeNeed.tjm_client_mode === 'fixed' ? 'Fixe' : 'Maximum'})`}
              </span>
            </div>
            {(activeNeed.free_days != null || activeNeed.commercial_terms?.free_days != null) && (
              <div className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
                <span className="text-slate-500 block text-[10px] uppercase font-semibold">Jours de gratuité</span>
                <span className="font-semibold text-emerald-400">
                  {activeNeed.free_days != null ? activeNeed.free_days : (activeNeed.commercial_terms?.free_days || 0)} jour(s)
                </span>
              </div>
            )}
            {activeNeed.mission_type && (
              <div className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
                <span className="text-slate-500 block text-[10px] uppercase font-semibold">Type de mission</span>
                <span className="font-semibold text-white">
                  {activeNeed.mission_type === "court" ? "Mission courte (période ferme)" : "Mission longue (12 mois)"}
                </span>
              </div>
            )}
            {renderCommercialTerms(activeNeed.commercial_terms) && (
              <div className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
                <span className="text-slate-500 block text-[10px] uppercase font-semibold">Modalités</span>
                <span className="font-semibold text-white">{renderCommercialTerms(activeNeed.commercial_terms)}</span>
              </div>
            )}
            {activeNeed.client_contact_id && (
              <div className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
                <span className="text-slate-500 block text-[10px] uppercase font-semibold">Interlocuteur</span>
                <span className="font-semibold text-white">{activeNeed.client_contact_id}</span>
              </div>
            )}
          </div>
        )}
      </section>

      <div id="matching-candidates" />
      {/* SECTION 1: Active Applications on this Need */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-blue-400">
              Pipeline de traitement
            </p>
            <h2 className="text-xl font-heading font-bold text-white mt-0.5">
              Candidatures positionnées ({applications.length})
            </h2>
          </div>
          <Link
            to="/admin/recrutement/candidats"
            className="text-xs font-semibold text-blue-400 hover:text-blue-300 flex items-center gap-1"
          >
            + Parcourir les candidats
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {loadingApps ? (
          <div className="flex justify-center py-10">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : applications.length === 0 ? (
          <div className="glass rounded-2xl p-8 border border-white/10 text-center text-slate-500">
            <Users className="h-8 w-8 mx-auto text-slate-600 mb-2" />
            <p className="text-sm font-semibold text-slate-300">Aucun candidat positionné sur ce besoin</p>
            <p className="text-xs text-slate-500 mt-1">
              Consultez les suggestions de matching IA ci-dessous pour positionner les meilleurs profils.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => {
              const cand = app.candidate || {};
              const currentStage = app.stage_v2 || app.stage || "qualification";
              const currentStageIdx = STAGES_V2.findIndex((s) => s.id === currentStage);

              return (
                <div
                  key={app._id}
                  className="glass rounded-2xl p-5 border border-white/10 space-y-4 hover:border-white/20 transition-all"
                  data-testid={`need-application-${app._id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-bold text-sm">
                        {(cand.first_name?.[0] || "C") + (cand.last_name?.[0] || "")}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/recrutement/candidats/${cand._id || app.candidate_id}`}
                            className="font-semibold text-white hover:text-blue-300 text-sm"
                          >
                            {cand.first_name} {cand.last_name}
                          </Link>
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                              STAGE_COLORS[currentStage] || STAGE_COLORS.qualification
                            }`}
                          >
                            Étape : {currentStage}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">{cand.email || "Email non renseigné"}</p>
                      </div>
                    </div>

                    {/* Quick Stage Controls */}
                    <div className="flex flex-wrap items-center gap-2">
                      {currentStage === "qualification" && (
                        <button
                          type="button"
                          onClick={() => handleAdvance(app._id)}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-xs"
                          data-testid={`advance-btn-${app._id}`}
                        >
                          Qualifier RH →
                        </button>
                      )}

                      {currentStage === "validation_tech" && (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleValidateTech(app._id, true)}
                            className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center gap-1"
                            data-testid={`validate-tech-btn-${app._id}`}
                          >
                            <ShieldCheck className="h-3.5 w-3.5" />
                            Valider Tech (CTO)
                          </button>
                          <button
                            type="button"
                            onClick={() => handleValidateTech(app._id, false)}
                            className="px-2.5 py-1.5 rounded-lg bg-rose-600/30 text-rose-300 hover:bg-rose-600/50 text-xs font-semibold"
                          >
                            Refus Tech
                          </button>
                        </div>
                      )}

                      {currentStage === "validation_commercial" && (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleValidateCommercial(app._id, true)}
                            className="px-2.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold flex items-center gap-1"
                            data-testid={`validate-comm-btn-${app._id}`}
                          >
                            <TrendingUp className="h-3.5 w-3.5" />
                            Valider Comm. (CCO)
                          </button>
                          <button
                            type="button"
                            onClick={() => handleValidateCommercial(app._id, false)}
                            className="px-2.5 py-1.5 rounded-lg bg-rose-600/30 text-rose-300 hover:bg-rose-600/50 text-xs font-semibold"
                          >
                            Refus Comm.
                          </button>
                        </div>
                      )}

                      {currentStage === "preparation_cv" && (
                        <button
                          type="button"
                          onClick={() => handleAdvance(app._id)}
                          className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold"
                          data-testid={`advance-prep-btn-${app._id}`}
                        >
                          CV Validé → Vers Présentation
                        </button>
                      )}

                      {currentStage === "presente" && (
                        <button
                          type="button"
                          onClick={() => setSelectedAppForPresentation(app)}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center gap-1.5"
                          data-testid={`present-client-btn-${app._id}`}
                        >
                          <Send className="h-3.5 w-3.5" />
                          Présenter au Client
                        </button>
                      )}

                      {currentStage === "entretiens" && (
                        <button
                          type="button"
                          onClick={() => setSelectedAppForInterview(app)}
                          className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-1.5"
                          data-testid={`interview-btn-${app._id}`}
                        >
                          <CalendarCheck className="h-3.5 w-3.5" />
                          Gérer Entretiens
                        </button>
                      )}

                      {currentStage === "decision" && (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setSelectedAppForAcceptance(app)}
                            className="px-2.5 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-500 text-white text-xs font-semibold flex items-center gap-1"
                            data-testid={`decide-accept-${app._id}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Recruter (Accepté)
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDecide(app._id, "refuse_client")}
                            className="px-2.5 py-1.5 rounded-lg bg-rose-600/40 text-rose-300 hover:bg-rose-600/60 text-xs font-semibold"
                          >
                            Refus Client
                          </button>
                        </div>
                      )}

                      {currentStage === "accepted" && (
                        <button
                          type="button"
                          onClick={() => setSelectedAppForConversion(app)}
                          className="px-2.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold flex items-center gap-1.5"
                          data-testid={`convert-mission-btn-${app._id}`}
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          Convertir en Mission Pilotage
                        </button>
                      )}

                      {/* Universal Actions */}
                      {currentStage !== "accepted" && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAppForDetach(app);
                            setDetachReason("Profil non retenu par le client");
                            setDetachCustomReason("");
                            setDetachError(null);
                          }}
                          className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-rose-500/20 text-slate-300 hover:text-rose-300 border border-white/10 hover:border-rose-500/30 text-xs font-semibold flex items-center gap-1.5 transition-colors"
                          title="Détacher le candidat de ce besoin client"
                          data-testid={`detach-candidate-btn-${app._id}`}
                        >
                          <UserMinus className="h-3.5 w-3.5 text-rose-400" />
                          Détacher
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setSelectedAppForRefusal(app)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                        title="Refuser et envoyer email de refus"
                        data-testid={`refuse-email-btn-${app._id}`}
                      >
                        <Mail className="h-4 w-4" />
                      </button>

                      {currentStageIdx > 0 && currentStage !== "accepted" && currentStage !== "refuse_client" && (
                        <button
                          type="button"
                          onClick={() => handleBack(app._id)}
                          className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-[11px] text-slate-400"
                          title="Retourner à l'étape précédente"
                        >
                          ← Retour
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Rejection or Validation trace banners */}
                  {(app.outcome === "ecarte" || app.stage === "rejected" || app.stage_v2 === "rejected") && (
                    <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/20 text-xs text-rose-300 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
                      <span>
                        <strong>Candidature écartée</strong>
                        {app.rejection_source && ` (${app.rejection_source})`} : {app.rejection_reason || "Non retenu"}
                      </span>
                    </div>
                  )}

                  {(app.validations?.tech || app.validations?.commercial) && app.outcome !== "ecarte" && (
                    <div className="flex flex-wrap gap-2 text-[11px] pt-1">
                      {app.validations?.tech && (
                        <span className="px-2.5 py-1 rounded bg-violet-500/10 text-violet-300 border border-violet-500/20 flex items-center gap-1.5">
                          <ShieldCheck className="w-3.5 h-3.5 text-violet-400" />
                          <span>
                            <strong>CTO :</strong> {app.validations.tech.status === "approved" ? "Approuvé" : "Refusé"}
                            {app.validations.tech.comment ? ` — "${app.validations.tech.comment}"` : ""}
                          </span>
                        </span>
                      )}
                      {app.validations?.commercial && (
                        <span className="px-2.5 py-1 rounded bg-purple-500/10 text-purple-300 border border-purple-500/20 flex items-center gap-1.5">
                          <TrendingUp className="w-3.5 h-3.5 text-purple-400" />
                          <span>
                            <strong>CCO :</strong> {app.validations.commercial.status === "approved" ? "Approuvé" : "Refusé"}
                            {app.validations.commercial.comment ? ` — "${app.validations.commercial.comment}"` : ""}
                          </span>
                        </span>
                      )}
                    </div>
                  )}

                  <Link className="inline-flex min-h-11 items-center text-blue-300 underline" to={`/admin/recrutement/pipeline?application_id=${app._id || app.id}`}>Voir le pipeline</Link>

                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* SECTION 2: AI Matching Candidates */}
      <MatchingCandidatesSection
        needId={needId}
        currentCandidates={applications}
        handleLinkCandidate={handleLinkCandidate}
      />

      {/* Modals */}
      {selectedAppForPresentation && (
        <ClientPresentationModal
          isOpen={Boolean(selectedAppForPresentation)}
          onClose={() => {
            setSelectedAppForPresentation(null);
            void fetchApplications();
          }}
          applicationId={selectedAppForPresentation._id}
          candidateId={selectedAppForPresentation.candidate_id}
          needId={needId}
        />
      )}

      {selectedAppForRefusal && (
        <EmailPreviewModal
          isOpen={Boolean(selectedAppForRefusal)}
          onClose={() => {
            setSelectedAppForRefusal(null);
            void fetchApplications();
          }}
          type="refusal"
          context={{
            application_id: selectedAppForRefusal._id,
            candidate_id: selectedAppForRefusal.candidate_id,
            candidate_name: `${selectedAppForRefusal.candidate?.first_name || ""} ${selectedAppForRefusal.candidate?.last_name || ""}`.trim(),
            candidate_email: selectedAppForRefusal.candidate?.email,
            mission_title: need.title,
          }}
          onSuccess={() => {
            setSelectedAppForRefusal(null);
            void fetchApplications();
          }}
        />
      )}

      {selectedAppForInterview && (
        <InterviewsWorkflowModal
          isOpen={Boolean(selectedAppForInterview)}
          onClose={() => {
            setSelectedAppForInterview(null);
            void fetchApplications();
          }}
          applicationId={selectedAppForInterview._id}
          candidateId={selectedAppForInterview.candidate_id}
          needId={needId}
          candidateName={`${selectedAppForInterview.candidate?.first_name || ""} ${selectedAppForInterview.candidate?.last_name || ""}`.trim()}
          missionTitle={need.title}
        />
      )}

      {/* Delete Need Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Supprimer le besoin client"
        message={`Voulez-vous vraiment supprimer définitivement le besoin « ${need.title || ""} » ? Cette action retirera la mission de la liste. Cette opération est irréversible.`}
        confirmText="Confirmer la suppression"
        cancelText="Annuler"
        variant="danger"
        isSubmitting={deletingNeed}
        onConfirm={handleDeleteNeed}
        testId="delete-need-detail-modal"
      />

      {/* Acceptance Confirmation & Multi-needs closure Modal */}
      {selectedAppForAcceptance && (
        <AcceptanceConfirmationModal
          isOpen={Boolean(selectedAppForAcceptance)}
          onClose={() => {
            setSelectedAppForAcceptance(null);
            void fetchApplications();
          }}
          applicationId={selectedAppForAcceptance._id}
          onAccepted={(_result) => {
            setSelectedAppForConversion(selectedAppForAcceptance);
            setSelectedAppForAcceptance(null);
            void fetchApplications();
          }}
        />
      )}

      {/* Mission Conversion to Pilotage Modal */}
      {selectedAppForConversion && (
        <MissionConversionModal
          isOpen={Boolean(selectedAppForConversion)}
          onClose={() => {
            setSelectedAppForConversion(null);
            void fetchApplications();
          }}
          applicationId={selectedAppForConversion._id}
          onConverted={() => {
            void fetchApplications();
          }}
        />
      )}

      {/* Detach Candidate Modal */}
      {selectedAppForDetach && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#101426] border border-amber-500/30 rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
                <UserMinus className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Détacher le candidat du besoin</h2>
                <p className="text-xs text-slate-400">
                  {selectedAppForDetach.candidate?.first_name} {selectedAppForDetach.candidate?.last_name}
                </p>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-900 border border-white/10 text-xs space-y-2 text-slate-300">
              <p className="font-semibold text-white">Conséquences de cette action :</p>
              <ul className="list-disc list-inside space-y-1 text-slate-400">
                <li>Le candidat reste conservé dans LENVIA</li>
                <li>Son statut dans le vivier n&apos;est pas modifié</li>
                <li>Ses CV et cycles ne sont pas supprimés</li>
                <li>Seul le lien avec ce besoin client est retiré du pipeline</li>
              </ul>
            </div>

            {(selectedAppForDetach.stage_v2 === "presente" || selectedAppForDetach.stage_v2 === "entretiens") && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
                <span>
                  Ce candidat est actuellement à l&apos;étape <strong>{selectedAppForDetach.stage_v2 === "presente" ? "Présenté client" : "Entretiens client"}</strong>.
                  Le détachement annulera son positionnement sur cette opportunité.
                </span>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-300 block">
                Motif du détachement (optionnel) :
              </label>
              <select
                value={detachReason}
                onChange={(e) => setDetachReason(e.target.value)}
                className="w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                data-testid="detach-reason-select"
              >
                <option value="Profil non retenu par le client">Profil non retenu par le client</option>
                <option value="Candidat indisponible">Candidat indisponible / engagé ailleurs</option>
                <option value="TJM ou conditions commerciales incompatibles">TJM ou conditions commerciales incompatibles</option>
                <option value="Volonté du candidat de se retirer">Volonté du candidat de se retirer</option>
                <option value="Autre">Autre motif...</option>
              </select>

              {detachReason === "Autre" && (
                <input
                  type="text"
                  value={detachCustomReason}
                  onChange={(e) => setDetachCustomReason(e.target.value)}
                  placeholder="Précisez le motif..."
                  className="w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500 mt-2"
                  data-testid="detach-custom-reason-input"
                />
              )}
            </div>

            {detachError && (
              <div
                className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs flex items-start gap-2"
                data-testid="detach-modal-error"
              >
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-rose-400" />
                <span>{detachError}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => {
                  setSelectedAppForDetach(null);
                  setDetachError(null);
                }}
                disabled={detaching}
                className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDetachCandidate}
                disabled={detaching}
                className="px-5 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-xs font-semibold text-white shadow-md flex items-center gap-1.5 disabled:opacity-50"
                data-testid="confirm-detach-btn"
              >
                {detaching ? "Détachement..." : "Confirmer le détachement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MatchingCandidatesSection({ needId, currentCandidates, handleLinkCandidate }) {
  const { matches, loading, error, refetch } = useNeedMatches(needId);
  const [linkingId, setLinkingId] = useState(null);
  const [recalculatingId, setRecalculatingId] = useState(null);
  const [overrides, setOverrides] = useState({});

  const onLink = async (candidateId) => {
    setLinkingId(candidateId);
    try {
      await handleLinkCandidate(candidateId);
    } finally {
      setLinkingId(null);
    }
  };

  const handleRecalculate = async (candidateId) => {
    setRecalculatingId(candidateId);
    try {
      const res = await apiClient.post(
        `/recruitment/needs/${needId}/matches/recalculate/${candidateId}`
      );
      setOverrides((prev) => ({ ...prev, [candidateId]: res.data }));
    } catch (err) {
      console.error("Recalculate failed:", err);
    } finally {
      setRecalculatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="glass rounded-2xl p-8 border border-white/10 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <section className="glass rounded-2xl p-6 border border-rose-500/20 bg-rose-500/5 text-rose-300 text-xs flex flex-col items-center justify-center gap-3 text-center" data-testid="matching-error-state">
        <AlertCircle className="h-6 w-6 text-rose-400" />
        <div>
          <p className="font-semibold text-sm">Impossible de charger les suggestions de matching IA</p>
          <p className="text-slate-400 mt-0.5">{error}</p>
        </div>
        <button
          type="button"
          onClick={() => refetch?.()}
          className="px-4 py-2 rounded-lg bg-rose-600/20 hover:bg-rose-600/30 text-rose-200 border border-rose-500/30 font-semibold text-xs transition-colors"
        >
          Réessayer le chargement
        </button>
      </section>
    );
  }

  // Filter out candidates already positioned
  const positionedIds = new Set(currentCandidates.map((c) => c.candidate_id));
  const availableMatches = (matches || [])
    .map((m) => overrides[m.candidate_id] || m)
    .filter((m) => !positionedIds.has(m.candidate_id));

  const renderScoreBadge = (match) => {
    const status = match.score_status;
    const score = match.score;

    if (status === "insufficient_data") {
      return (
        <div
          className="flex flex-col items-center justify-center w-16 h-16 rounded-xl border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-center shrink-0 p-1"
          title="Profil insuffisamment renseigné (compétences ou CV manquants)"
          data-testid={`match-badge-insufficient-${match.candidate_id}`}
        >
          <span className="text-[11px] font-bold text-slate-300 leading-tight">Profil</span>
          <span className="text-[9px] uppercase font-bold text-slate-400">Incomplet</span>
        </div>
      );
    }

    if (status === "not_calculated" || score == null) {
      return (
        <div
          className="flex flex-col items-center justify-center w-16 h-16 rounded-xl border border-slate-700 bg-slate-800/50 text-slate-400 font-mono text-center shrink-0 p-1"
          title="Score non encore calculé"
          data-testid={`match-badge-uncalculated-${match.candidate_id}`}
        >
          <span className="text-[11px] font-bold text-slate-400 leading-tight">Non</span>
          <span className="text-[9px] uppercase font-bold text-slate-500">calculé</span>
        </div>
      );
    }

    if (status === "stale") {
      return (
        <div
          className="flex flex-col items-center justify-center w-16 h-16 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-300 shrink-0 font-mono text-center p-1"
          title="Score obsolète : le CV ou le besoin a évolué"
          data-testid={`match-badge-stale-${match.candidate_id}`}
        >
          <span className="text-xl font-bold">{Math.round(score)}</span>
          <span className="text-[8px] uppercase tracking-wider font-bold text-amber-400">À actualiser</span>
        </div>
      );
    }

    const isTrueZero = score === 0;
    const scoreColor = isTrueZero
      ? "text-slate-400 bg-slate-500/10 border-slate-500/30"
      : score >= 80
      ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
      : score >= 60
      ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
      : "text-slate-400 bg-slate-500/10 border-slate-500/30";

    return (
      <div
        className={`flex flex-col items-center justify-center w-16 h-16 rounded-xl border shrink-0 font-mono ${scoreColor}`}
        title={isTrueZero ? "Vrai zéro : aucun critère ne correspond" : "Score calculé automatiquement"}
        data-testid={`match-badge-score-${match.candidate_id}`}
      >
        <span className="text-2xl font-black">{Math.round(score)}</span>
        <span className="text-[9px] uppercase tracking-wider font-bold">
          {isTrueZero ? "0 / 100" : "Score"}
        </span>
      </div>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-400 bg-emerald-500/10 border-emerald-500/30";
    if (score >= 60) return "text-amber-400 bg-amber-500/10 border-amber-500/30";
    return "text-slate-400 bg-slate-500/10 border-slate-500/30";
  };

  const getProfitabilityBadge = (profitability) => {
    if (!profitability) return null;
    const marginPct = profitability.margin_pct != null ? profitability.margin_pct : profitability.marge_pct;
    const marginWithoutFree = profitability.margin_pct_without_free_days != null
      ? profitability.margin_pct_without_free_days
      : profitability.marge_sans_gratuite;
    const label = profitability.status_label || profitability.label || (marginPct != null ? `${marginPct.toFixed(1)}%` : "Calcul indisponible — données manquantes");
    const tjm = profitability.tjm_client;
    const cjm = profitability.cjm_calculated != null ? profitability.cjm_calculated : profitability.cjm;
    const statusStr = String(profitability.status || "").toLowerCase();

    let colorClass = "bg-slate-800 text-slate-400 border-slate-700";
    if (statusStr.includes("warning") || statusStr.includes("alert")) {
      colorClass = "bg-rose-500/10 text-rose-300 border-rose-500/30";
    } else if (statusStr.includes("acceptable") || statusStr.includes("vigilance")) {
      colorClass = "bg-amber-500/10 text-amber-300 border-amber-500/30";
    } else if (statusStr.includes("good") || statusStr.includes("target")) {
      colorClass = "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";
    } else if (statusStr.includes("excellent") || statusStr.includes("high")) {
      colorClass = "bg-blue-500/10 text-blue-300 border-blue-500/30";
    }

    return (
      <div className="space-y-1.5 pt-1 text-[11px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-slate-400 font-medium">Rentabilité :</span>
          <span className={`px-2 py-0.5 rounded font-semibold border ${colorClass}`}>
            {marginPct != null
              ? `${marginPct.toFixed(1)}% de marge (${label})`
              : label}
          </span>
          {marginWithoutFree != null && profitability.free_days > 0 && (
            <span className="text-slate-500 text-[10px]">
              Sans gratuité : {marginWithoutFree.toFixed(1)}%
            </span>
          )}
        </div>

        {/* Financial metrics breakdown */}
        {tjm != null && cjm != null && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-400">
            <span>TJM Client : <strong className="text-white">{tjm} €</strong></span>
            <span>CJM Consultant : <strong className="text-white">{Math.round(cjm)} €</strong></span>
            {profitability.ca_previsionnel != null && (
              <span>CA prév. : <strong className="text-slate-200">{Math.round(profitability.ca_previsionnel).toLocaleString('fr-FR')} €</strong></span>
            )}
            {profitability.cout_consultant != null && (
              <span>Coût consultant : <strong className="text-slate-200">{Math.round(profitability.cout_consultant).toLocaleString('fr-FR')} €</strong></span>
            )}
            {profitability.margin_eur != null && (
              <span>Marge : <strong className={profitability.margin_eur >= 0 ? "text-emerald-400" : "text-rose-400"}>
                {Math.round(profitability.margin_eur).toLocaleString('fr-FR')} €
              </strong></span>
            )}
            {profitability.free_days > 0 && (
              <span className="text-emerald-400/90 font-medium">
                ({profitability.worked_days} j travaillés • {profitability.free_days} j gratuits • {profitability.billed_days} j facturés)
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="space-y-4" data-testid="matching-candidates-section">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[11px] font-bold uppercase tracking-widest text-blue-400">
            Intelligence artificielle explicable
          </p>
          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
            Zéro dépense IA inutile
          </span>
        </div>
        <h2 className="text-xl font-heading font-bold text-white mt-0.5">
          Candidats correspondants recommandés
        </h2>
        <p className="text-xs text-slate-400 mt-1">
          Le score est calculé de façon déterministe et transparente sur les compétences vérifiées du profil.
        </p>
      </div>

      {availableMatches.length === 0 ? (
        <div className="glass rounded-2xl p-8 border border-white/10 text-center text-slate-500 text-xs">
          Tous les candidats correspondants sont déjà positionnés ou aucun profil ne correspond aux critères.
        </div>
      ) : (
        <div className="grid gap-3">
          {availableMatches.map((match) => {
            const cand = match.candidate || {};
            const isStale = match.is_cv_stale || match.score_status === "stale";

            return (
              <div
                key={match.candidate_id}
                className="glass rounded-2xl p-5 border border-white/10 hover:border-white/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
                data-testid={`match-card-${match.candidate_id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Explanatory Score Badge */}
                  {renderScoreBadge(match)}

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/recrutement/candidats/${match.candidate_id}`}
                        className="font-semibold text-white hover:text-blue-300 text-base"
                      >
                        {cand.first_name} {cand.last_name}
                      </Link>
                      {cand.pool_status === "in_pool" && (
                        <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px] font-semibold border border-amber-500/30">
                          Vivier
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">{cand.email || "Email non renseigné"}</p>

                    {/* Insufficient data alert */}
                    {match.score_status === "insufficient_data" && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/80 border border-slate-700 text-xs text-slate-300">
                        <AlertCircle className="h-4 w-4 text-slate-400 shrink-0" />
                        <span>Profil insuffisamment renseigné : ni compétences ni CV détaillés pour ce candidat.</span>
                      </div>
                    )}

                    {/* Not calculated prompt */}
                    {(match.score_status === "not_calculated" || match.score == null) && match.score_status !== "insufficient_data" && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300">
                        <span>Score non calculé pour ce profil.</span>
                        <button
                          type="button"
                          disabled={recalculatingId === match.candidate_id}
                          onClick={() => handleRecalculate(match.candidate_id)}
                          className="px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white font-semibold text-[11px] transition-colors"
                        >
                          {recalculatingId === match.candidate_id ? "Calcul..." : "Calculer le score IA"}
                        </button>
                      </div>
                    )}

                    {/* Stale score warning badge with manual recalculate button */}
                    {isStale && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span>CV modifié / Score à actualiser</span>
                        <button
                          type="button"
                          disabled={recalculatingId === match.candidate_id}
                          onClick={() => handleRecalculate(match.candidate_id)}
                          className="px-2.5 py-1 rounded bg-amber-500/20 hover:bg-amber-500/40 text-white font-semibold text-[11px] transition-colors"
                        >
                          {recalculatingId === match.candidate_id
                            ? "Recalcul..."
                            : "Recalculer le score IA"}
                        </button>
                      </div>
                    )}

                    {/* Explainable match reasons / skill breakdown */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {(match.reasons || []).map((reason, i) => {
                        const isDem = reason.startsWith("Démontré");
                        const isInf = reason.startsWith("Inféré");
                        const isMiss = reason.startsWith("À vérifier") || reason.startsWith("Manque");
                        const badgeColor = isDem
                          ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                          : isInf
                          ? "bg-sky-500/10 text-sky-300 border-sky-500/30"
                          : isMiss
                          ? "bg-rose-500/10 text-rose-300 border-rose-500/30"
                          : "bg-slate-800 text-slate-300 border-slate-700/60";

                        return (
                          <span
                            key={i}
                            className={`text-[11px] px-2 py-0.5 rounded-full border ${badgeColor}`}
                          >
                            {reason}
                          </span>
                        );
                      })}
                    </div>

                    {/* Profitability margin indicator */}
                    {getProfitabilityBadge(match.profitability)}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                  <Link
                    to={`/admin/recrutement/candidats/${match.candidate_id}`}
                    className="px-3 py-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 rounded-xl transition-colors"
                  >
                    Voir fiche
                  </Link>
                  <button
                    type="button"
                    onClick={() => onLink(match.candidate_id)}
                    disabled={linkingId === match.candidate_id}
                    className="px-4 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-xl shadow-xs transition-colors disabled:opacity-50 flex items-center gap-1.5"
                    data-testid={`position-candidate-btn-${match.candidate_id}`}
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    {linkingId === match.candidate_id ? "Positionnement..." : "Positionner"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
